import type { User } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import { FIRESTORE_BASE_URL } from './firestore-rest';
function decode(value: any): any {
  if ('mapValue' in value) return Object.fromEntries(Object.entries(value.mapValue.fields || {}).map(([k,v])=>[k,decode(v)]));
  if ('arrayValue' in value) return (value.arrayValue.values || []).map(decode);
  if ('timestampValue' in value) return Timestamp.fromDate(new Date(value.timestampValue));
  if ('integerValue' in value) return Number(value.integerValue);
  for (const key of ['stringValue','doubleValue','booleanValue','nullValue']) if (key in value) return value[key];
  return undefined;
}
function encode(value: any): any {
  if (value === null) return {nullValue:null};
  if (value instanceof Date || typeof value?.toDate === 'function') return {timestampValue:(value instanceof Date ? value : value.toDate()).toISOString()};
  if (Array.isArray(value)) return {arrayValue:{values:value.map(encode)}};
  if (typeof value === 'object') return {mapValue:{fields:Object.fromEntries(Object.entries(value).filter(([,v])=>v!==undefined).map(([k,v])=>[k,encode(v)]))}};
  return typeof value === 'boolean' ? {booleanValue:value} : typeof value === 'number' ? {doubleValue:value} : {stringValue:value};
}
async function request(user: User, path: string, data?: Record<string, any>) {
  const controller = new AbortController();
  const timer = setTimeout(()=>controller.abort(),15000);
  try {
    const token = await Promise.race([user.getIdToken(), new Promise<never>((_, reject) => controller.signal.addEventListener('abort', () => reject(new Error('Sign-in took too long. Please check your connection and retry.')), {once:true}))]);
    const mask = data ? '?' + Object.keys(data).map(key=>`updateMask.fieldPaths=${encodeURIComponent(key)}`).join('&') : '';
    const response = await fetch(`${FIRESTORE_BASE_URL}/${path}${mask}`, {method:data?'PATCH':'GET',signal:controller.signal,headers:{Authorization:`Bearer ${token}`,'Content-Type':'application/json'},...(data?{body:JSON.stringify(encode(data).mapValue)}:{})});
    if (response.status === 404 && !data) return null;
    if (!response.ok) throw new Error('Your profile could not be loaded. Please try again.');
    return await response.json();
  } finally {clearTimeout(timer);}
}
export async function readAuthProfile(user: User, root = false) {
  const value = await request(user,`users/${encodeURIComponent(user.uid)}${root?'':'/profile/main'}`);
  return {exists:()=>value!==null,data:()=>value ? decode({mapValue:{fields:value.fields || {}}}) : undefined};
}
export async function writeAuthProfile(user: User, data: Record<string,any>, root = false) {
  await request(user,`users/${encodeURIComponent(user.uid)}${root?'':'/profile/main'}`,data);
}

// Older/server-created profile documents may contain only billing fields.
// An explicit profile choice wins; otherwise recover setup state from the root.
export function restoreSetupState(profile: Record<string, any>, root?: Record<string, any>) {
  const restored = {...profile};
  for (const key of ['hasOnboarded', 'hasSeenOnboardingPrompt']) {
    if (typeof restored[key] !== 'boolean' && typeof root?.[key] === 'boolean') restored[key] = root[key];
  }
  return restored;
}
